import React, { useState, useRef, useEffect } from 'react';
import { Button, Modal, message, Select, Input, Divider, Space, Tabs } from 'antd';
import { 
  BoldOutlined, 
  ItalicOutlined, 
  UnderlineOutlined, 
  PictureOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  FontSizeOutlined,
  BgColorsOutlined,
  FontColorsOutlined,
  CodeOutlined,
  LinkOutlined,
  TableOutlined
} from '@ant-design/icons';
import ImageUploadModal from './ImageUploadModal';
import VideoInsertModal from './VideoInsertModal';
import './RichTextEditor.css';

const { TextArea } = Input;
const { TabPane } = Tabs;

const RichTextEditor = ({ value = '', onChange, placeholder = 'Nhập nội dung...' }) => {
  const [content, setContent] = useState(value);
  const [isImageModalVisible, setIsImageModalVisible] = useState(false);
  const [isVideoModalVisible, setIsVideoModalVisible] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [viewMode, setViewMode] = useState('visual'); // 'visual' or 'html'
  const [fontSize, setFontSize] = useState('14px');
  const [fontFamily, setFontFamily] = useState('Arial');
  const editorRef = useRef(null);
  const inputTimerRef = useRef(null);
  const selectionRef = useRef(null);
  const activeTextColorRef = useRef(null);
  const activeBgColorRef = useRef(null);
  const activeFontSizeRef = useRef(null);

  // Selection helpers to keep formatting (like color) when clicking toolbar
  const isSelectionInsideEditor = () => {
    const sel = window.getSelection();
    if (!editorRef.current || !sel || sel.rangeCount === 0) return false;
    const container = sel.getRangeAt(0).commonAncestorContainer;
    const node = container.nodeType === Node.ELEMENT_NODE ? container : container.parentNode;
    return editorRef.current.contains(node);
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (isSelectionInsideEditor()) {
      selectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const range = selectionRef.current;
    if (range && editorRef.current) {
      editorRef.current.focus();
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  // Apply inline style at caret when no selection (so next typed text keeps the style)
  const applyStyleAtCaret = (style) => {
    if (!editorRef.current) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    // Insert a styled span and place caret inside it
    const span = document.createElement('span');
    Object.assign(span.style, style);
    span.appendChild(document.createTextNode('\u200B')); // zero-width space
    range.insertNode(span);
    // Move caret inside span (after the zero-width space)
    const newRange = document.createRange();
    newRange.setStart(span.firstChild, 1);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
  };

  useEffect(() => {
    // Only set initial content, don't update during typing
    if (value && value !== content && !editorRef.current?.contains(document.activeElement)) {
      setContent(value);
    }
  }, [value, viewMode]);

  // Initialize editor with LTR defaults (don't force text-align so toolbar can change it)
  useEffect(() => {
    if (editorRef.current && viewMode === 'visual') {
      const editor = editorRef.current;
      editor.style.direction = 'ltr';
      editor.style.unicodeBidi = 'embed';
      editor.style.writingMode = 'horizontal-tb';
      editor.setAttribute('dir', 'ltr');
      editor.setAttribute('lang', 'vi-VN');
  // Prefer CSS inline styles for execCommand outputs
  try { document.execCommand('styleWithCSS', false, true); } catch {}
      
      // Set initial content only once
      if (value && !editor.innerHTML.trim()) {
        editor.innerHTML = value;
      }
    }
  }, [viewMode]);

  // Handle focus to ensure LTR direction (but don't override text alignment)
  const handleFocus = () => {
    if (editorRef.current) {
      const editor = editorRef.current;
      // Force LTR direction
      editor.style.direction = 'ltr';
  // Restore selection on focus
  restoreSelection();
      
      // Reset any RTL elements
      const allElements = editor.querySelectorAll('*');
      allElements.forEach(el => {
        if (el.dir === 'rtl' || el.style.direction === 'rtl') {
          el.dir = 'ltr';
          el.style.direction = 'ltr';
        }
      });
    }
  };

  // Vietnamese input composition handling
  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = () => {
    setIsComposing(false);
  };

  // Update local content only; do not call onChange while typing to avoid re-renders
  const handleInput = () => {
    if (!isComposing && editorRef.current) {
      const newContent = editorRef.current.innerHTML;
      setContent(newContent);
    }
  };

  // Prevent Space from bubbling to Tabs or other parents (causing focus jump)
  const handleKeyDown = (e) => {
    // Prevent space bubbling to tabs
    if (e.key === ' ' || e.key === 'Spacebar' || e.code === 'Space') {
      e.stopPropagation();
    }
    // If caret and active colors set, ensure caret is within styled span
    const sel = window.getSelection();
    const collapsed = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).collapsed : true;
    if (collapsed && (activeTextColorRef.current || activeBgColorRef.current || activeFontSizeRef.current)) {
      const style = {};
      if (activeTextColorRef.current) style.color = activeTextColorRef.current;
      if (activeBgColorRef.current) style.backgroundColor = activeBgColorRef.current;
      if (activeFontSizeRef.current) style.fontSize = activeFontSizeRef.current;
      applyStyleAtCaret(style);
    }
  };

  // Handle blur to save final content
  const handleBlur = () => {
    if (editorRef.current) {
  // Save selection before losing focus (used when clicking toolbar)
  saveSelection();
      const newContent = editorRef.current.innerHTML;
      setContent(newContent);
      if (onChange) {
        onChange(newContent);
      }
    }
  };

  // Handle HTML text area change
  const handleHtmlChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    if (onChange) {
      onChange(newContent);
    }
  };

  // When switching to HTML view, sync current DOM content into state once
  useEffect(() => {
    if (viewMode === 'html' && editorRef.current) {
      const html = editorRef.current.innerHTML;
      setContent(html);
      if (onChange) onChange(html);
    }
  }, [viewMode]);

  // Format commands
  const execCommand = (command, value = null) => {
    if (viewMode === 'html') return;
    // Ensure selection is restored when clicking toolbar
    restoreSelection();
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
      handleInput();
    }
  };

  // Advanced formatting functions
  const formatText = (command, value = null) => {
    execCommand(command, value);
  };

  const changeFontSize = (size) => {
    setFontSize(size);
    restoreSelection();
    try { document.execCommand('styleWithCSS', false, true); } catch {}
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (range.collapsed) {
      // Apply at caret and persist for next typing
      applyStyleAtCaret({ fontSize: size });
      activeFontSizeRef.current = size;
    } else {
      // Wrap current selection
      const span = document.createElement('span');
      span.style.fontSize = size;
      try {
        range.surroundContents(span);
      } catch (e) {
        // Fallback for complex selections (may cut across nodes)
        const html = range.toString();
        execCommand('insertHTML', `<span style="font-size: ${size}">${html}</span>`);
      }
    }
  };

  const changeFontFamily = (family) => {
    setFontFamily(family);
    execCommand('fontName', family);
  };

  const insertLink = () => {
    const url = prompt('Nhập URL:');
    if (url) {
      const text = window.getSelection().toString() || url;
      execCommand('insertHTML', `<a href="${url}" target="_blank">${text}</a>`);
    }
  };

  const insertTable = () => {
    const rows = prompt('Số hàng:', '3');
    const cols = prompt('Số cột:', '3');
    if (rows && cols) {
      let tableHTML = '<table border="1" style="border-collapse: collapse; width: 100%; margin: 10px 0;">';
      for (let i = 0; i < parseInt(rows); i++) {
        tableHTML += '<tr>';
        for (let j = 0; j < parseInt(cols); j++) {
          tableHTML += '<td style="padding: 8px; border: 1px solid #ddd;">&nbsp;</td>';
        }
        tableHTML += '</tr>';
      }
      tableHTML += '</table>';
      execCommand('insertHTML', tableHTML);
    }
  };

  const changeTextColor = (color) => {
    restoreSelection();
    const sel = window.getSelection();
    const collapsed = !sel || sel.rangeCount === 0 ? true : sel.getRangeAt(0).collapsed;
    try {
      // Prefer CSS-based styling to persist for caret typing
      document.execCommand('styleWithCSS', false, true);
    } catch {}
    execCommand('foreColor', color);
    // If no selection, ensure style sticks for next characters
    if (collapsed) {
      applyStyleAtCaret({ color });
    }
    activeTextColorRef.current = color;
  };

  const changeBackgroundColor = (color) => {
    restoreSelection();
    const sel = window.getSelection();
    const collapsed = !sel || sel.rangeCount === 0 ? true : sel.getRangeAt(0).collapsed;
    try {
      document.execCommand('styleWithCSS', false, true);
    } catch {}
    if (!document.queryCommandSupported || document.queryCommandSupported('hiliteColor')) {
      execCommand('hiliteColor', color);
    } else {
      execCommand('backColor', color);
    }
    if (collapsed) {
      applyStyleAtCaret({ backgroundColor: color });
    }
    activeBgColorRef.current = color;
  };

  // Insert image
  const handleInsertImage = (imageUrl) => {
    if (editorRef.current && viewMode === 'visual') {
      editorRef.current.focus();
      const img = `<img src="${imageUrl}" alt="Uploaded image" style="max-width: 100%; height: auto; margin: 10px 0; border-radius: 4px;" />`;
      execCommand('insertHTML', img);
      setIsImageModalVisible(false);
    }
  };

  // Insert video embed HTML
  const handleInsertVideo = (embedHtml) => {
    if (editorRef.current && viewMode === 'visual') {
      editorRef.current.focus();
      execCommand('insertHTML', embedHtml);
      setIsVideoModalVisible(false);
    }
  };

  const fontSizeOptions = [
    { label: '10px', value: '10px' },
    { label: '12px', value: '12px' },
    { label: '14px', value: '14px' },
    { label: '16px', value: '16px' },
    { label: '18px', value: '18px' },
    { label: '20px', value: '20px' },
    { label: '24px', value: '24px' },
    { label: '28px', value: '28px' },
    { label: '32px', value: '32px' },
    { label: '36px', value: '36px' }
  ];

  const fontFamilyOptions = [
    { label: 'Arial', value: 'Arial' },
    { label: 'Times New Roman', value: 'Times New Roman' },
    { label: 'Helvetica', value: 'Helvetica' },
    { label: 'Georgia', value: 'Georgia' },
    { label: 'Verdana', value: 'Verdana' },
    { label: 'Courier New', value: 'Courier New' },
    { label: 'Tahoma', value: 'Tahoma' }
  ];

  return (
    <div 
      className="rich-text-editor" 
      style={{ border: '1px solid #d9d9d9', borderRadius: '6px' }}
      dir="ltr"
    >
      {/* View Mode Tabs */}
      <Tabs 
        activeKey={viewMode} 
        onChange={setViewMode}
        size="small"
        style={{ marginBottom: 0 }}
        tabBarStyle={{ 
          margin: 0, 
          paddingLeft: '12px',
          paddingRight: '12px',
          backgroundColor: '#fafafa',
          borderBottom: '1px solid #f0f0f0'
        }}
      >
        <TabPane tab="Soạn thảo" key="visual" />
        <TabPane tab="HTML" key="html" />
      </Tabs>

      {viewMode === 'visual' && (
        <>
          {/* Advanced Toolbar */}
          <div style={{ 
            padding: '12px', 
            borderBottom: '1px solid #f0f0f0', 
            background: '#fafafa'
          }}>
            {/* First Row - Font & Size */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Select
                value={fontFamily}
                onChange={changeFontFamily}
                onMouseDown={() => saveSelection()}
                onDropdownVisibleChange={(open) => open && saveSelection()}
                style={{ width: 140 }}
                size="small"
                options={fontFamilyOptions}
              />
              <Select
                value={fontSize}
                onChange={changeFontSize}
                onMouseDown={() => saveSelection()}
                onDropdownVisibleChange={(open) => open && saveSelection()}
                style={{ width: 80 }}
                size="small"
                options={fontSizeOptions}
              />
              <Divider type="vertical" style={{ height: '20px' }} />
              
              {/* Basic Formatting */}
              <Button
                onMouseDown={(e) => e.preventDefault()}
                type="text"
                size="small"
                icon={<BoldOutlined />}
                onClick={() => formatText('bold')}
                title="Bold (Ctrl+B)"
              />
              <Button
                onMouseDown={(e) => e.preventDefault()}
                type="text"
                size="small"
                icon={<ItalicOutlined />}
                onClick={() => formatText('italic')}
                title="Italic (Ctrl+I)"
              />
              <Button
                onMouseDown={(e) => e.preventDefault()}
                type="text"
                size="small"
                icon={<UnderlineOutlined />}
                onClick={() => formatText('underline')}
                title="Underline (Ctrl+U)"
              />
              
              <Divider type="vertical" style={{ height: '20px' }} />
              
              {/* Text & Background Color */}
              <input 
                type="color" 
                onMouseDown={() => saveSelection()}
                onChange={(e) => changeTextColor(e.target.value)}
                style={{ width: '32px', height: '24px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                title="Text Color"
              />
              <input 
                type="color" 
                onMouseDown={() => saveSelection()}
                onChange={(e) => changeBackgroundColor(e.target.value)}
                style={{ width: '32px', height: '24px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                title="Background Color"
              />
            </div>

            {/* Second Row - Alignment & Lists */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {/* Alignment */}
              <Button
                onMouseDown={(e) => e.preventDefault()}
                type="text"
                size="small"
                icon={<AlignLeftOutlined />}
                onClick={() => formatText('justifyLeft')}
                title="Align Left"
              />
              <Button
                onMouseDown={(e) => e.preventDefault()}
                type="text"
                size="small"
                icon={<AlignCenterOutlined />}
                onClick={() => formatText('justifyCenter')}
                title="Align Center"
              />
              <Button
                onMouseDown={(e) => e.preventDefault()}
                type="text"
                size="small"
                icon={<AlignRightOutlined />}
                onClick={() => formatText('justifyRight')}
                title="Align Right"
              />
              
              <Divider type="vertical" style={{ height: '20px' }} />
              
              {/* Lists */}
              <Button
                onMouseDown={(e) => e.preventDefault()}
                type="text"
                size="small"
                icon={<UnorderedListOutlined />}
                onClick={() => formatText('insertUnorderedList')}
                title="Bullet List"
              />
              <Button
                onMouseDown={(e) => e.preventDefault()}
                type="text"
                size="small"
                icon={<OrderedListOutlined />}
                onClick={() => formatText('insertOrderedList')}
                title="Numbered List"
              />
              
              <Divider type="vertical" style={{ height: '20px' }} />
              
              {/* Insert Elements */}
              <Button
                onMouseDown={(e) => e.preventDefault()}
                type="text"
                size="small"
                icon={<LinkOutlined />}
                onClick={insertLink}
                title="Insert Link"
              />
              <Button
                onMouseDown={(e) => e.preventDefault()}
                type="text"
                size="small"
                icon={<TableOutlined />}
                onClick={insertTable}
                title="Insert Table"
              />
              <Button
                onMouseDown={(e) => e.preventDefault()}
                type="text"
                size="small"
                icon={<PictureOutlined />}
                onClick={() => setIsImageModalVisible(true)}
                title="Insert Image"
              />
              <Button
                onMouseDown={(e) => e.preventDefault()}
                type="text"
                size="small"
                icon={<CodeOutlined />}
                onClick={() => setIsVideoModalVisible(true)}
                title="Chèn video"
              />
            </div>
          </div>

          {/* Visual Editor */}
          <div
            ref={editorRef}
            contentEditable
            style={{
              minHeight: '300px',
              padding: '16px',
              outline: 'none',
              lineHeight: '1.6',
              fontSize: '14px',
              fontFamily: 'Arial, sans-serif',
              direction: 'ltr',
              unicodeBidi: 'embed',
              writingMode: 'horizontal-tb'
            }}
            onInput={handleInput}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            data-placeholder={placeholder}
            dir="ltr"
            lang="vi-VN"
            suppressContentEditableWarning={true}
          />
        </>
      )}

      {/* HTML Editor */}
      {viewMode === 'html' && (
        <div style={{ padding: '16px' }}>
          <TextArea
            value={content}
            onChange={handleHtmlChange}
            placeholder="Nhập HTML code..."
            rows={20}
            style={{ 
              fontFamily: 'Consolas, Monaco, monospace', 
              fontSize: '13px',
              lineHeight: '1.5'
            }}
          />
        </div>
      )}

      {/* Image Upload Modal */}
      <ImageUploadModal
        visible={isImageModalVisible}
        onCancel={() => setIsImageModalVisible(false)}
        onInsert={handleInsertImage}
      />
      <VideoInsertModal
        visible={isVideoModalVisible}
        onCancel={() => setIsVideoModalVisible(false)}
        onInsert={handleInsertVideo}
      />
    </div>
  );
};

export default RichTextEditor;
